// Same gateway origin resolution as verify-api.ts / register-api.ts — an absolute URL, because the
// standalone InviteAcceptApp runs pre-router and before Keycloak, and must reach the gateway directly.
const GATEWAY_URL =
  (import.meta.env.VITE_GATEWAY_URL as string | undefined) ??
  (import.meta.env.VITE_GRAPHQL_URL as string | undefined)?.replace(/\/graphql\/?$/, '') ??
  'http://localhost:4000';

const INVITE_ACCEPT_URL = `${GATEWAY_URL}/auth/invite/accept`;

export type InviteAcceptOutcome =
  | { ok: true }
  | { ok: false; reason: 'invalid_or_expired' | 'weak_password' | 'network_error'; message?: string };

/**
 * POST /auth/invite/accept — public, no Authorization header.
 *
 * The invited person has NO credentials yet: that is the whole point of the flow, and the reason this
 * page must never sit behind Keycloak's `login-required` (see `main.tsx`'s public-path short-circuits).
 * acl-svc exposes the endpoint anonymously for exactly this — `InviteController` is annotated
 * *"PUBLIC invite-accept surface, reached anonymously with the emailed token (like /auth/register).
 * No @RequirePermission"* — taking `{ token, password }` and returning `{ ok: true }`.
 *
 * Like the verify flow, the emailed link is the GET and THIS is the consume, so an email client's
 * link prefetcher cannot burn the token.
 */
export async function postAcceptInvite(token: string, password: string): Promise<InviteAcceptOutcome> {
  let res: Response;
  try {
    res = await fetch(INVITE_ACCEPT_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, password }),
    });
  } catch {
    return { ok: false, reason: 'network_error' };
  }

  if (res.ok) return { ok: true };

  // A rejected password and a dead token are different problems for the reader: one is worth
  // retrying in place, the other means "ask for a new invite". Keep them apart rather than
  // collapsing both into one unhelpful message.
  const body = await res.json().catch(() => null);
  const message = typeof (body as { message?: unknown } | null)?.message === 'string'
    ? (body as { message: string }).message
    : undefined;

  if (res.status === 400 && message && /password/i.test(message)) {
    return { ok: false, reason: 'weak_password', message };
  }
  return { ok: false, reason: 'invalid_or_expired', message };
}
